import type { ReactNode } from 'react';
import { Alert, AlertProps, AlertTitle, Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import { PlanLimit } from '@/graphql/generated/types.generated';

/** Billing route + hash that auto-opens the plans modal (see SettingsWorkspaceBilling). */
const UPGRADE_PLAN_PATH = '/settings/workspace/billing#upgrade';

/**
 * Same page, but the plans modal opens straight on the add-ons calculator with the
 * workspace's current plan and interval preselected. Trials and free plans get the plan
 * list instead; that fallback lives in PlansPopup.
 */
export const ADDONS_PLAN_PATH = '/settings/workspace/billing#addons';

export interface PlanLimitUpgradeProps {
    /** Which limit was hit — used to segment upsell conversion in PostHog. */
    planLimit: PlanLimit;
    /** Explains the limit in the feature's own words. Falls back to a generic message. */
    message?: string;
    /** Overrides the CTA label. */
    ctaLabel?: string;
    /** Optional bold first line for the `alert` variant, e.g. an offer headline. */
    title?: string;
    /** Replaces the severity's default icon in the `alert` variant. */
    icon?: ReactNode;
    /**
     * `alert` blocks — use when the user just tried something the plan forbids.
     * `inline` is a quiet one-liner — use for boundaries that are always present,
     * where an alert on every render would nag.
     * `button` is just the CTA, full width — use inside a dialog or card that already
     * carries the explanation.
     */
    variant?: 'alert' | 'inline' | 'button';
    severity?: AlertProps['severity'];
    /** Runs right before navigating — use it to close the surrounding dialog or popover. */
    onUpgrade?: () => void;
    /** Destination of the CTA. Defaults to the plans modal (`#upgrade`). */
    path?: string;
    /**
     * CTA style in the `alert` variant. `contained` is the brand's primary button: use it when
     * the user is blocked right now, so the way out is the strongest thing in the alert.
     */
    ctaVariant?: 'text' | 'contained';
    sx?: SxProps<Theme>;
}

/**
 * Routes a user who hit a plan limit to the upgrade flow, instead of leaving them
 * at a dead end.
 */
export default function PlanLimitUpgrade(props: PlanLimitUpgradeProps) {
    const {
        planLimit,
        message,
        ctaLabel,
        title,
        icon,
        variant = 'alert',
        severity = 'warning',
        onUpgrade,
        path = UPGRADE_PLAN_PATH,
        ctaVariant = 'text',
        sx,
    } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleUpgrade = () => {
        if (POSTHOG_ENABLED) posthog.capture('plan-limit.upgrade-cta.clicked', { planLimit, variant });
        onUpgrade?.();
        navigate(path);
    };

    const text = message ?? t('plan-limit.reached.message');
    const cta = ctaLabel ?? t('plan-limit.reached.cta');

    if (variant === 'button') {
        return (
            <Button variant="contained" fullWidth disableElevation onClick={handleUpgrade} sx={sx}>
                {cta}
            </Button>
        );
    }

    if (variant === 'inline') {
        return (
            <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap" sx={sx}>
                <Typography variant="caption" color="text.secondary">
                    {text}
                </Typography>
                <Button
                    variant="text"
                    size="small"
                    onClick={handleUpgrade}
                    sx={{ minWidth: 0, p: 0, typography: 'caption' }}
                >
                    {cta}
                </Button>
            </Stack>
        );
    }

    const button = (
        <Button
            variant={ctaVariant}
            color={ctaVariant === 'contained' ? 'primary' : 'inherit'}
            size="small"
            onClick={handleUpgrade}
            sx={{ flexShrink: 0 }}
        >
            {cta}
        </Button>
    );

    return (
        <Alert
            severity={severity}
            icon={icon}
            // Array-merged rather than spread so a callback or array `sx` from the caller survives.
            // With a title the CTA sits in the title row instead of MUI's `action` column, so the
            // description is not narrowed by the button's width and can run the full width.
            sx={[
                { borderRadius: 2, ...(title && { '& .MuiAlert-message': { flexGrow: 1 } }) },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            action={title ? undefined : button}
        >
            {title && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                    <AlertTitle sx={{ mb: 0 }}>{title}</AlertTitle>
                    {button}
                </Stack>
            )}
            {text}
        </Alert>
    );
}
